import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { loadConfig, postQuote } from '@/lib/api';
import { formatPhone } from '@/lib/format';
import { searchAddresses, type AddressSuggestion } from '@/lib/address';

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  notes: string;
}

// Load the Square Web Payments SDK once (sandbox vs production host from config).
let sdkPromise: Promise<void> | null = null;
function loadSquareSdk(production: boolean): Promise<void> {
  if (sdkPromise) return sdkPromise;
  const src = production
    ? 'https://web.squarecdn.com/v1/square.js'
    : 'https://sandbox.web.squarecdn.com/v1/square.js';
  sdkPromise = new Promise((resolve, reject) => {
    if ((window as any).Square) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load payment SDK'));
    document.head.appendChild(s);
  });
  return sdkPromise;
}

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [payError, setPayError] = useState('');
  const [totals, setTotals] = useState<{ subtotal: number; shipping: number; total: number } | null>(null);
  const cardRef = useRef<any>(null);
  const mountedRef = useRef(false);

  const [form, setForm] = useState<FormData>({
    email: '', firstName: '', lastName: '', phone: '',
    address: '', city: '', state: '', zip: '', country: 'US', notes: '',
  });

  // Address autocomplete (keyless Photon lookup, debounced).
  const [addrSuggestions, setAddrSuggestions] = useState<AddressSuggestion[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addrBlurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAddressInput = (value: string) => {
    setForm((prev) => ({ ...prev, address: value }));
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (value.trim().length < 3) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }
    addrTimer.current = setTimeout(async () => {
      const results = await searchAddresses(value, form.country);
      setAddrSuggestions(results);
      setAddrOpen(results.length > 0);
    }, 300);
  };

  const pickSuggestion = (s: AddressSuggestion) => {
    setForm((prev) => ({
      ...prev,
      address: s.line1,
      city: s.city || prev.city,
      state: s.state || prev.state,
      zip: s.zip || prev.zip,
      country: s.country || prev.country,
    }));
    setAddrSuggestions([]);
    setAddrOpen(false);
  };

  useEffect(() => () => {
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (addrBlurTimer.current) clearTimeout(addrBlurTimer.current);
  }, []);

  // Mount the Square card field once (the cart is non-empty on this path).
  useEffect(() => {
    if (mountedRef.current || cart.length === 0) return;
    mountedRef.current = true;
    (async () => {
      try {
        const cfg = await loadConfig();
        if (!cfg || !cfg.squareEnabled) { setPayError('Online checkout is not available right now.'); return; }
        await loadSquareSdk(cfg.squareEnvironment === 'production');
        const payments = (window as any).Square.payments(cfg.squareApplicationId, cfg.squareLocationId);
        const card = await payments.card();
        await card.attach('#card-container');
        cardRef.current = card;
        setCardReady(true);
      } catch {
        setPayError('Could not load the payment form. Please refresh and try again.');
      }
    })();
  }, [cart.length]);

  // Server-computed shipping + total (re-prices from D1; never trusts the browser).
  useEffect(() => {
    if (cart.length === 0) return;
    const items = cart.map((c) => ({ id: c.productId, qty: c.quantity }));
    postQuote(items).then((q) => { if (q) setTotals({ subtotal: q.subtotal, shipping: q.shipping, total: q.total }); });
  }, [cart]);

  if (cart.length === 0 && !submitted) {
    return (
      <main className="min-h-screen bg-[#f0f4f7]">
        <section className="bg-[#2c2a4a] py-14 md:py-20">
          <div className="container-main text-center">
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Checkout</h1>
          </div>
        </section>
        <section className="py-20 md:py-28 text-center">
          <div className="container-main">
            <div className="w-20 h-20 bg-[#d1bedd] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shopping-basket-line text-3xl text-[#88d6f8]" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-[#1A1A1A] mb-2">Your cart is empty</h2>
            <p className="text-[#8B7355] mb-8">Add some items before checking out.</p>
            <Link
              to="/shop"
              className="inline-block py-3.5 px-10 rounded-md bg-[#4f528d] text-white font-sans font-semibold text-sm uppercase tracking-wider hover:bg-[#3d4070] transition-colors whitespace-nowrap"
            >
              Continue Shopping
            </Link>
          </div>
        </section>
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#f0f4f7]">
        <section className="bg-[#2c2a4a] py-14 md:py-20">
          <div className="container-main text-center">
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Order Confirmed</h1>
          </div>
        </section>
        <section className="py-20 md:py-28 text-center">
          <div className="container-main">
            <div className="w-20 h-20 bg-[#d1bedd] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-double-line text-3xl text-[#88d6f8]" />
            </div>
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-3">Thank you for your order!</h2>
            <p className="text-[#8B7355] mb-2 max-w-md mx-auto">
              Your payment was received and we have emailed your confirmation.
            </p>
            <p className="text-[#8B7355] mb-8 max-w-md mx-auto">
              Most orders ship within 1-2 business days.
            </p>
            <Link
              to="/shop"
              className="inline-block py-3.5 px-10 rounded-md bg-[#4f528d] text-white font-sans font-semibold text-sm uppercase tracking-wider hover:bg-[#3d4070] transition-colors whitespace-nowrap"
            >
              Continue Shopping
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setPayError('');

    if (!form.address || !form.city || !form.state || !form.zip || !form.firstName || !form.lastName) {
      setPayError('Please fill in your full name and shipping address.');
      return;
    }
    if (!cardRef.current) {
      setPayError('The payment form is still loading. Please wait a moment.');
      return;
    }

    setLoading(true);
    try {
      // 1. Tokenize the card (card data never touches our server -> PCI SAQ A).
      const result = await cardRef.current.tokenize();
      if (result.status !== 'OK' || !result.token) {
        const detail = result.errors?.[0]?.message;
        setPayError(detail || 'Please check your card details and try again.');
        setLoading(false);
        return;
      }

      // 2. Charge server-side (re-prices every line from D1).
      const res = await fetch('/api/checkout/square', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          items: cart.map((c) => ({ id: c.productId, qty: c.quantity })),
          email: form.email || undefined,
          sourceId: result.token,
          address: {
            name: `${form.firstName} ${form.lastName}`.trim(),
            address1: form.address,
            address2: '',
            city: form.city,
            state: form.state,
            zip: form.zip,
            country: form.country || 'US',
          },
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.error) {
        const err = data.error || 'payment_failed';
        if (err === 'insufficient_stock') setPayError('Sorry, one of your items just sold out. Please adjust your cart.');
        else if (err === 'missing_address') setPayError('Please fill in your full shipping address.');
        else if (err === 'checkout_unavailable') setPayError('Online checkout is not available right now.');
        else setPayError('Your payment could not be completed. Please try another card.');
        setLoading(false);
        return;
      }

      clearCart();
      setSubmitted(true);
    } catch {
      setPayError('Something went wrong placing your order. Please try again.');
      setLoading(false);
    }
  };

  const subtotal = totals ? totals.subtotal : cartTotal;
  const shipping = totals ? totals.shipping : null;
  const total = totals ? totals.total : cartTotal;

  return (
    <main className="min-h-screen bg-[#f0f4f7]">
      {/* Header */}
      <section className="bg-[#2c2a4a] py-14 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Checkout</h1>
          <p className="text-white/70 text-base md:text-lg">Secure card payment</p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact */}
                <div>
                  <h2 className="font-serif text-xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#4f528d] text-white flex items-center justify-center text-sm font-sans">1</span>
                    Contact Information
                  </h2>
                  <div className="bg-[#efe4f7] rounded-lg border border-[#E8E0D4] p-5 md:p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Email</label>
                      <input type="email" required value={form.email} onChange={(e) => handleChange('email', e.target.value)}
                        className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                        placeholder="you@example.com" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">First Name</label>
                        <input type="text" required value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
                          className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Last Name</label>
                        <input type="text" required value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
                          className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Phone</label>
                      <input type="tel" inputMode="tel" autoComplete="tel" value={form.phone}
                        onChange={(e) => handleChange('phone', formatPhone(e.target.value))}
                        className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                        placeholder="(555) 123-4567" />
                    </div>
                  </div>
                </div>

                {/* Shipping */}
                <div>
                  <h2 className="font-serif text-xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#4f528d] text-white flex items-center justify-center text-sm font-sans">2</span>
                    Shipping Address
                  </h2>
                  <div className="bg-[#efe4f7] rounded-lg border border-[#E8E0D4] p-5 md:p-6 space-y-4">
                    <div className="relative">
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Address</label>
                      <input type="text" required value={form.address} autoComplete="off"
                        onChange={(e) => handleAddressInput(e.target.value)}
                        onFocus={() => { if (addrSuggestions.length) setAddrOpen(true); }}
                        onBlur={() => { addrBlurTimer.current = setTimeout(() => setAddrOpen(false), 150); }}
                        className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                        placeholder="Start typing your address…" />
                      {addrOpen && addrSuggestions.length > 0 && (
                        <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#D4C9B8] rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                          {addrSuggestions.map((s, i) => (
                            <li key={`${s.label}-${i}`}>
                              <button type="button"
                                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                                className="w-full text-left px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#efe4f7] flex items-start gap-2">
                                <i className="ri-map-pin-line text-[#4f528d] mt-0.5 shrink-0" />
                                <span>{s.label}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">City</label>
                        <input type="text" required value={form.city} onChange={(e) => handleChange('city', e.target.value)}
                          className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">State</label>
                        <input type="text" required value={form.state} onChange={(e) => handleChange('state', e.target.value)}
                          className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                          placeholder="WI" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">ZIP Code</label>
                        <input type="text" required value={form.zip} onChange={(e) => handleChange('zip', e.target.value)}
                          className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] placeholder:text-[#8B7355] focus:outline-none focus:border-[#B87333]"
                          placeholder="53714" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-1.5">Country</label>
                      <select value={form.country} onChange={(e) => handleChange('country', e.target.value)}
                        className="w-full bg-white border border-[#D4C9B8] rounded-md py-2.5 px-4 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B87333] cursor-pointer">
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment */}
                <div>
                  <h2 className="font-serif text-xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#4f528d] text-white flex items-center justify-center text-sm font-sans">3</span>
                    Payment
                  </h2>
                  <div className="bg-[#efe4f7] rounded-lg border border-[#E8E0D4] p-5 md:p-6">
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Card details</label>
                    {/* Square Web Payments SDK mounts its secure card iframe here. */}
                    <div id="card-container" className="bg-white border border-[#D4C9B8] rounded-md p-3 min-h-[56px]" />
                    {!cardReady && !payError && (
                      <p className="text-xs text-[#8B7355] mt-2">Loading secure card field…</p>
                    )}
                    {payError && <p className="text-sm text-red-600 mt-3">{payError}</p>}
                    <div className="mt-3 flex items-center gap-2 text-xs text-[#8B7355]">
                      <i className="ri-lock-line" />
                      <span>Your card is encrypted and processed securely by Square.</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                  <Link to="/cart" className="text-sm text-[#4f528d] hover:underline flex items-center gap-1">
                    <i className="ri-arrow-left-line" /> Return to cart
                  </Link>
                  <button type="submit" disabled={loading || !cardReady}
                    className="sm:ml-auto py-3.5 px-10 rounded-md bg-[#4f528d] text-white font-sans font-semibold text-sm uppercase tracking-wider hover:bg-[#3d4070] transition-colors whitespace-nowrap disabled:opacity-60">
                    {loading ? 'Processing…' : `Pay $${total.toFixed(2)}`}
                  </button>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#efe4f7] rounded-lg border border-[#E8E0D4] p-6 sticky top-24">
                <h2 className="font-serif text-xl font-bold text-[#1A1A1A] mb-5">Order Summary</h2>

                <div className="space-y-4 mb-5">
                  {cart.map((item) => (
                    <div key={`${item.productId}-${item.skuId ?? 'base'}`} className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-md overflow-hidden bg-white border border-[#E8E0D4] shrink-0">
                        {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{item.name}</p>
                        {item.skuLabel && <p className="text-xs text-[#4f528d]">{item.skuLabel}</p>}
                        <p className="text-xs text-[#8B7355]">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-sm font-semibold text-[#1E3D2F]">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-[#E8E0D4] pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>Shipping</span>
                    <span>{shipping == null ? 'Calculated…' : shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                </div>

                <div className="border-t border-[#E8E0D4] pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-serif text-lg font-bold text-[#1A1A1A]">Total</span>
                    <span className="font-serif text-xl font-bold text-[#1E3D2F]">${total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-[#8B7355]">
                  <i className="ri-shield-check-line" />
                  <span>Secure card processing by Square</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
